import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Globe, ExternalLink, RefreshCw, Plug, Check, Users, MessageSquarePlus,
  ArrowRight, ChevronDown, CircleDot, Sparkles, Link2,
} from "lucide-react";
import { api } from "@/platform/api";
import { Button } from "@/platform/components/primitives";
import { useAuth } from "@/platform/AuthContext";
import { STATUSES } from "@/platform/app/TicketsBoard";
import { ReviewerLinkCopy } from "@/platform/app/ReviewerLinkCopy";


function relTime(iso) {
  if (!iso) return null;
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch (_e) { return null; }
}

// Clean, human page label from a route + an optional captured document.title.
function pageLabel(route, title) {
  if (title && title.trim()) return title.trim();
  if (!route || route === "/") return "Homepage";
  const seg = route.split("/").filter(Boolean).pop();
  if (!seg) return "Homepage";
  return seg.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function Panel({ title, children, action }) {
  return (
    <section>
      {(title || action) && (
        <div className="mb-2.5 flex items-center justify-between">
          {title && <p className="text-[12px] font-semibold uppercase tracking-wider text-[#6E7686]">{title}</p>}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export default function ProjectReview({
  project, sites, connected, onOpenConnect, onOpenInvite, onVerify, verifying, verifyMsg, onCustomizeWidget, reloadToken,
}) {
  const { user, refresh } = useAuth();
  const MEMBER_SWATCHES = ["#FFD400", "#4F46E5", "#0079BF", "#00C2E0", "#61BD4F", "#FF9F1A", "#EB5A46", "#C377E0"];
  const [savingColor, setSavingColor] = useState(false);
  const myColor = (user?.reviewColor || "").toUpperCase();
  const pickMyColor = async (c) => {
    setSavingColor(true);
    try { await api.patch("/api/auth/me/review-color", { color: c }); await refresh?.(); } catch (_e) { /* ignore */ }
    setSavingColor(false);
  };
  const [tickets, setTickets] = useState(null);
  const [invites, setInvites] = useState(null);
  const [opening, setOpening] = useState(false);
  const [openErr, setOpenErr] = useState("");

  const siteList = sites || [];
  const production = siteList.find((s) => s.environment === "production") || siteList[0] || null;
  const [siteId, setSiteId] = useState(production?.id);
  const [siteMenu, setSiteMenu] = useState(false);
  const site = siteList.find((s) => s.id === siteId) || production;

  const load = useCallback(async () => {
    try {
      const [tk, iv] = await Promise.all([
        api.get(`/api/projects/${project.id}/tickets`),
        api.get(`/api/projects/${project.id}/reviewer-invites`),
      ]);
      setTickets(tk.tickets || []);
      setInvites(iv.invites || []);
    } catch (_e) { /* ignore */ }
  }, [project.id]);
  useEffect(() => { load(); }, [load, reloadToken]);

  const counts = useMemo(() => {
    const c = { open: 0, in_progress: 0, ready_for_review: 0, resolved: 0 };
    (tickets || []).forEach((t) => { c[t.status in c ? t.status : "open"] += 1; });
    return c;
  }, [tickets]);

  const pages = useMemo(() => {
    const map = new Map();
    (tickets || []).forEach((t) => {
      const route = t.route || "/";
      if (!map.has(route)) map.set(route, { route, title: t.anchor?.pageTitle, open: 0, in_progress: 0, ready_for_review: 0, resolved: 0, total: 0 });
      const p = map.get(route);
      p[t.status in p ? t.status : "open"] += 1;
      p.total += 1;
      if (!p.title && t.anchor?.pageTitle) p.title = t.anchor.pageTitle;
    });
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [tickets]);

  const activeInvites = useMemo(() => (invites || []).filter((i) => i.status === "active"), [invites]);

  const openReview = async (route) => {
    setOpening(true); setOpenErr("");
    try {
      const res = await api.post(`/api/projects/${project.id}/review-session`, { siteId: site?.id });
      if (!res.siteUrl) { setOpenErr("No website URL is configured for this project."); setOpening(false); return; }
      let u;
      try { u = new URL(res.siteUrl); } catch (_e) { setOpenErr("The configured website URL is invalid."); setOpening(false); return; }
      // Deep-open a specific page only on plain site URLs (no existing query).
      if (route && route !== "/" && !u.search) {
        try { u.pathname = route; } catch (_e) { /* keep base */ }
      }
      u.searchParams.set("plr", res.sessionToken);
      u.searchParams.set("review", "1");
      window.open(u.toString(), "_blank", "noopener,noreferrer");
    } catch (e) {
      setOpenErr(e?.message || "Couldn't start the review session.");
    }
    setOpening(false);
  };

  const reviewerName = user?.fullName || user?.email || "you";
  const status = site?.connectionStatus || "not_connected";
  const isConnected = connected || status === "connected";
  const isLost = status === "disconnected";

  // ── Header: website + primary CTAs ────────────────────────────────────────
  const Header = (
    <div className="rounded-2xl border border-[#242A38] bg-gradient-to-br from-[#171B29] to-[#12151D] p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6E7686]">Project review</p>
          <h2 className="mt-1 truncate text-[20px] font-semibold tracking-tight text-[#F4F5F8]">{project.name}</h2>
          <div className="mt-1.5 flex flex-wrap items-center gap-2.5 text-[13px]">
            <a href={site?.url || (site?.domain ? `https://${site.domain}` : "#")} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-[#98A0B0] hover:text-[#E6E8EE]">
              <Globe className="h-3.5 w-3.5" /> {site?.domain || "No website"}
            </a>
            {isConnected ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#16A34A22] px-2.5 py-0.5 text-[11.5px] font-semibold text-[#4ADE80]"><span className="h-1.5 w-1.5 rounded-full bg-[#4ADE80]" /> Connected</span>
            ) : isLost ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#D9770622] px-2.5 py-0.5 text-[11.5px] font-semibold text-[#FBBF24]"><span className="h-1.5 w-1.5 rounded-full bg-[#FBBF24]" /> Connection issue</span>
            ) : (
              <span className="rounded-full bg-[#1C2130] px-2.5 py-0.5 text-[11.5px] font-medium text-[#98A0B0]">Not connected</span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          {siteList.length > 1 && (
            <div className="relative">
              <button onClick={() => setSiteMenu((v) => !v)} className="inline-flex items-center gap-1.5 rounded-xl border border-[#2A3040] bg-[#0E1016] px-3 py-2.5 text-[13px] font-medium text-[#E6E8EE]" data-testid="review-site-selector">
                {site?.name || site?.environment || "Site"} <ChevronDown className="h-3.5 w-3.5 text-[#6E7686]" />
              </button>
              {siteMenu && (
                <div className="absolute right-0 z-20 mt-1 w-56 overflow-hidden rounded-xl border border-[#242A38] bg-[#151A24] shadow-2xl">
                  {siteList.map((s) => (
                    <button key={s.id} onClick={() => { setSiteId(s.id); setSiteMenu(false); }}
                      className={`flex w-full flex-col items-start px-3.5 py-2.5 text-left hover:bg-[#1C2130] ${s.id === site?.id ? "bg-[#1C2130]" : ""}`}
                      data-testid={`review-site-option-${s.id}`}>
                      <span className="text-[13px] font-medium text-[#E6E8EE]">{s.name || s.environment}</span>
                      <span className="text-[11.5px] text-[#6E7686]">{s.domain}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <Button onClick={() => openReview(null)} loading={opening} disabled={!site} className="sm:w-auto sm:px-5" data-testid="open-review-btn">
            <ExternalLink className="h-4 w-4" /> Open Review
          </Button>
          <Button variant="outline" onClick={onOpenInvite} className="sm:w-auto sm:px-5" data-testid="review-invite-btn">
            <Users className="h-4 w-4" /> Invite Reviewer
          </Button>
        </div>
      </div>
      <p className="mt-3 text-[12.5px] text-[#6E7686]" data-testid="reviewing-as">
        You'll review as <span className="font-medium text-[#AAB1BF]">{reviewerName}</span> — opens the live site in a new tab.
      </p>
      {openErr && <p className="mt-2 text-[12.5px] text-[#F87171]" data-testid="open-review-error">{openErr}</p>}
    </div>
  );

  // ── Empty state: not connected ────────────────────────────────────────────
  if (!isConnected && !isLost) {
    return (
      <div className="space-y-6" data-testid="review-not-connected">
        {Header}
        <div className="rounded-2xl border border-dashed border-[#2A3040] bg-[#12151D] p-12 text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl" style={{ background: "rgba(124,107,255,0.18)" }}><Plug className="h-7 w-7" style={{ color: "#A99BFF" }} /></div>
          <p className="mt-4 text-[17px] font-semibold text-[#F4F5F8]">Connect Pinlayr to start reviewing</p>
          <p className="mx-auto mt-1.5 max-w-md text-[13.5px] text-[#98A0B0]">Install the Pinlayr widget on {site?.domain || "your website"}, then return here to verify the connection.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button onClick={onOpenConnect} className="sm:w-auto sm:px-6" data-testid="review-connect-btn"><Plug className="h-4 w-4" /> Connect Pinlayr</Button>
            <Button variant="outline" onClick={onVerify} loading={verifying} className="sm:w-auto sm:px-5" data-testid="review-verify-btn"><RefreshCw className="h-4 w-4" /> Verify connection</Button>
          </div>
          {verifyMsg === "waiting" && <p className="mt-3 text-[13px] text-[#E5A400]">No signal yet. Make sure the snippet is installed and the page has been opened at least once.</p>}
        </div>
      </div>
    );
  }

  const noTickets = tickets !== null && tickets.length === 0;

  // ── Side column blocks ────────────────────────────────────────────────────
  const ConnectionBlock = (
    <Panel title="Website connection">
      <div className="rounded-2xl border border-[#242A38] bg-[#151A24] p-4" data-testid="review-connection-card">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-[#6E7686]" />
          <span className="truncate text-[13.5px] font-medium text-[#E6E8EE]">{site?.domain}</span>
          {isLost ? (
            <span className="ml-auto text-[11.5px] font-semibold text-[#FBBF24]">Not detected</span>
          ) : (
            <span className="ml-auto inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#4ADE80]"><Check className="h-3.5 w-3.5" /> Connected</span>
          )}
        </div>
        {site?.lastSeenAt && <p className="mt-2 text-[12px] text-[#6E7686]">Last detected {relTime(site.lastSeenAt)}</p>}
        {isLost && <p className="mt-2 text-[12px] text-[#FBBF24]">Pinlayr couldn't detect the widget on this website.</p>}
        <div className="mt-3 flex flex-wrap items-center gap-4">
          <a href={site?.url || (site?.domain ? `https://${site.domain}` : "#")} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[#A99BFF] hover:underline" data-testid="review-open-website"><ExternalLink className="h-3.5 w-3.5" /> Open website</a>
          <button onClick={onVerify} className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[#98A0B0] hover:text-[#E6E8EE]" data-testid="review-recheck"><RefreshCw className={`h-3.5 w-3.5 ${verifying ? "animate-spin" : ""}`} /> {isLost ? "Verify again" : "Re-check"}</button>
          <button onClick={onOpenConnect} className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[#98A0B0] hover:text-[#E6E8EE]" data-testid="review-install-instructions"><Link2 className="h-3.5 w-3.5" /> Install instructions</button>
        </div>
      </div>
    </Panel>
  );

  const ReviewerBlock = (
    <Panel
      title="Reviewer access"
      action={<button onClick={onOpenInvite} className="text-[12px] font-medium text-[#A99BFF] hover:underline" data-testid="review-invite-link">Invite reviewer</button>}
    >
      <div className="rounded-2xl border border-[#242A38] bg-[#151A24] p-1.5" data-testid="review-reviewer-access">
        <p className="px-2.5 pb-1.5 pt-2 text-[12px] text-[#98A0B0]">
          {invites === null ? "…" : activeInvites.length === 0 ? "Accountless reviewer links enabled" : `${activeInvites.length} active review ${activeInvites.length === 1 ? "link" : "links"}`}
        </p>
        {activeInvites.length > 0 && (
          <div className="space-y-1">
            {activeInvites.slice(0, 5).map((inv) => (
              <div key={inv.id} className="rounded-xl px-2.5 py-2 hover:bg-[#12151D]" data-testid={`review-invite-${inv.id}`}>
                <div className="flex items-center gap-2">
                  <span className="truncate text-[13px] font-medium text-[#E6E8EE]">{inv.label}</span>
                  <span className="ml-auto text-[11px] text-[#565E70]">{inv.expiresAt ? `expires ${fmtDate(inv.expiresAt)}` : "no expiration"}</span>
                </div>
                <div className="mt-1 flex items-center gap-3">
                  <span className="text-[11.5px] text-[#6E7686]">{inv.lastUsedAt ? `Last used ${relTime(inv.lastUsedAt)}` : "Never opened"}</span>
                  <span className="ml-auto"><ReviewerLinkCopy projectId={project.id} inv={inv} testid={`review-copy-${inv.id}`} /></span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Panel>
  );

  const accentInk = (() => {
    try {
      const h = (project.widgetColor || "#FFD400").replace("#", "");
      const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
      return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? "#111" : "#fff";
    } catch (_e) { return "#111"; }
  })();
  const WidgetBlock = (
    <Panel title="Review widget">
      <div className="rounded-2xl border border-[#242A38] bg-[#151A24] p-4" data-testid="review-widget-summary">
        <div className="flex items-center gap-2.5">
          <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-[12px] font-semibold" style={{ background: project.widgetColor || "#FFD400", color: accentInk }}>
            <MessageSquarePlus size={13} strokeWidth={2.5} /> {project.widgetLabel || "Review this page"}
          </span>
        </div>
        <div className="mt-3 space-y-1.5 text-[12.5px] text-[#98A0B0]">
          <div className="flex items-center justify-between"><span className="text-[#6E7686]">Position</span><span className="text-[#C4C9D4]">{(project.widgetPosition || "bottom-right") === "bottom-left" ? "Bottom left" : "Bottom right"}</span></div>
          <div className="flex items-center justify-between"><span className="text-[#6E7686]">Accent</span><span className="inline-flex items-center gap-1.5 text-[#C4C9D4]"><span className="h-3 w-3 rounded-full" style={{ background: project.widgetColor || "#FFD400" }} /> {(project.widgetColor || "#FFD400").toUpperCase()}</span></div>
        </div>
        <button onClick={onCustomizeWidget} className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-medium text-[#A99BFF] hover:underline" data-testid="review-customize-widget">Customize widget <ArrowRight className="h-3.5 w-3.5" /></button>
        <div className="mt-4 border-t border-[#242A38] pt-3">
          <p className="text-[12px] font-semibold text-[#C4C9D4]">Your pin colour</p>
          <p className="mt-0.5 text-[11px] text-[#6E7686]">Used for your pins when you open Review. Changing it recolours all your existing pins.</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {MEMBER_SWATCHES.map((s) => (
              <button
                key={s}
                onClick={() => pickMyColor(s)}
                disabled={savingColor}
                className={`h-7 w-7 rounded-full border-2 transition-transform hover:scale-110 disabled:opacity-60 ${myColor === s ? "border-white" : "border-transparent"}`}
                style={{ background: s }}
                aria-label={`Set my pin colour ${s}`}
                data-testid={`member-color-${s.slice(1)}`}
              />
            ))}
            {savingColor && <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#6D5EF6] border-t-transparent" />}
          </div>
        </div>
      </div>
    </Panel>
  );

  return (
    <div className="space-y-6" data-testid="review-tab">
      {Header}

      {isLost && (
        <div className="rounded-2xl border border-[#7A5310] bg-[#D9770614] p-4" data-testid="review-lost-banner">
          <p className="text-[14px] font-semibold text-[#FBBF24]">Pinlayr isn't currently detected</p>
          <p className="mt-1 text-[13px] text-[#C9A96A]">The widget may have been removed or the website may have changed. Your tickets and history are safe.</p>
          <div className="mt-3 flex flex-wrap gap-3">
            <Button variant="outline" onClick={onVerify} loading={verifying} className="sm:w-auto sm:px-5"><RefreshCw className="h-4 w-4" /> Verify connection</Button>
            <Button variant="outline" onClick={onOpenConnect} className="sm:w-auto sm:px-5"><Link2 className="h-4 w-4" /> Installation instructions</Button>
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        {/* Main column */}
        <div className="space-y-6">
          {tickets === null ? (
            <div className="grid place-items-center rounded-2xl border border-[#242A38] bg-[#12151D] py-20" data-testid="review-loading">
              <span className="h-6 w-6 animate-spin rounded-full border-2 border-[#6D5EF6] border-t-transparent" />
            </div>
          ) : noTickets ? (
            <div className="rounded-2xl border border-dashed border-[#2A3040] bg-[#12151D] p-12 text-center" data-testid="review-first-review">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl" style={{ background: "rgba(124,107,255,0.18)" }}><Sparkles className="h-7 w-7" style={{ color: "#A99BFF" }} /></div>
              <p className="mt-4 text-[17px] font-semibold text-[#F4F5F8]">Ready for your first review</p>
              <p className="mx-auto mt-1.5 max-w-md text-[13.5px] text-[#98A0B0]">Pinlayr is connected to {site?.domain}. Open the website and click anywhere to create your first pin.</p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Button onClick={() => openReview(null)} loading={opening} className="sm:w-auto sm:px-6"><ExternalLink className="h-4 w-4" /> Open Review</Button>
                <Button variant="outline" onClick={onOpenInvite} className="sm:w-auto sm:px-5"><Users className="h-4 w-4" /> Invite Reviewer</Button>
              </div>
            </div>
          ) : (
            <>
              {/* Ready for Review emphasis */}
              {counts.ready_for_review > 0 && (
                <div className="rounded-2xl border border-[#0E5A6B] bg-[#00C2E014] p-5" data-testid="review-ready-emphasis">
                  <div className="flex flex-wrap items-center gap-4">
                    <div>
                      <p className="text-[18px] font-semibold text-[#F4F5F8]">{counts.ready_for_review} {counts.ready_for_review === 1 ? "change" : "changes"} ready for review</p>
                      <p className="mt-0.5 text-[13px] text-[#98A0B0]">Share the project with a reviewer or open the live review.</p>
                    </div>
                    <div className="ml-auto flex flex-wrap gap-2.5">
                      <Button onClick={() => openReview(null)} loading={opening} className="sm:w-auto sm:px-5"><ExternalLink className="h-4 w-4" /> Open Review</Button>
                      <Button variant="outline" onClick={onOpenInvite} className="sm:w-auto sm:px-5"><Users className="h-4 w-4" /> Invite Reviewer</Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Review status summary */}
              <Panel title="Review status">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="review-status-summary">
                  {STATUSES.map((s) => (
                    <div key={s.key} className="rounded-2xl border border-[#242A38] bg-[#151A24] p-4" data-testid={`review-count-${s.key}`}>
                      <div className="flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
                        <span className="text-[11.5px] font-medium uppercase tracking-wide text-[#6E7686]">{s.label}</span>
                      </div>
                      <p className="mt-2 text-[26px] font-semibold leading-none text-[#F4F5F8]">{counts[s.key]}</p>
                    </div>
                  ))}
                </div>
              </Panel>

              {/* Pages being reviewed */}
              <Panel title="Pages">
                <div className="overflow-hidden rounded-2xl border border-[#242A38]" data-testid="review-pages">
                  {pages.map((p) => (
                    <div key={p.route} className="group flex items-center gap-3 border-b border-[#1C2130] bg-[#12151D] px-4 py-3.5 last:border-b-0" data-testid={`review-page-${p.route}`}>
                      <CircleDot className="h-4 w-4 shrink-0 text-[#565E70]" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13.5px] font-medium text-[#E6E8EE]">{pageLabel(p.route, p.title)}</p>
                        <p className="truncate text-[11.5px] text-[#565E70]">{p.route}</p>
                      </div>
                      <div className="hidden items-center gap-2 text-[11.5px] text-[#98A0B0] sm:flex">
                        {p.open > 0 && <span>{p.open} Open</span>}
                        {p.in_progress > 0 && <span className="text-[#FF9F1A]">{p.in_progress} In progress</span>}
                        {p.ready_for_review > 0 && <span className="text-[#00C2E0]">{p.ready_for_review} Ready</span>}
                        {p.resolved > 0 && <span className="text-[#61BD4F]">{p.resolved} Resolved</span>}
                      </div>
                      <button onClick={() => openReview(p.route)} className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg bg-[#1C2130] px-3 py-1.5 text-[12px] font-medium text-[#C4C9D4] opacity-0 transition-opacity hover:bg-[#252B3B] group-hover:opacity-100" data-testid={`review-open-page-${p.route}`}>
                        Open review <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </Panel>
            </>
          )}
        </div>

        {/* Side column */}
        <div className="space-y-6">
          {ConnectionBlock}
          {ReviewerBlock}
          {WidgetBlock}
        </div>
      </div>
    </div>
  );
}
